import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { z } from 'zod';
import { ArrowLeft, Plus } from 'lucide-react';
import { useCurrentUser } from '@/lib/use-current-user';
import { createTournament } from '@/data/tournaments';
import { toast } from '@/components/ui/sonner';
import { AppHeader } from '@/components/design';
import type {
  TournamentFormat,
  MatchFormat,
  ScoreMode,
  Discipline,
  GroupConfig,
} from '@/data/types';

const newTournamentSchema = z.object({
  name: z.string().min(1, 'Введите название').max(80),
  date: z.string().min(1, 'Укажите дату'),
  discipline: z.enum(['SINGLES', 'DOUBLES']),
  format: z.enum(['SINGLE_ELIM', 'DOUBLE_ELIM', 'ROUND_ROBIN', 'SWISS', 'GROUPS_PLAYOFF']),
  matchFormat: z.enum(['BO3', 'BO5', 'BO7']),
  scoreMode: z.enum(['WINNER_ONLY', 'SETS']),
});

const FORMATS: { k: TournamentFormat; name: string; desc: string }[] = [
  {
    k: 'SINGLE_ELIM',
    name: 'Single Elimination',
    desc: 'Олимпийка. Проиграл — выбыл. Самый быстрый формат.',
  },
  {
    k: 'DOUBLE_ELIM',
    name: 'Double Elimination',
    desc: 'Двойная сетка. У каждого есть второй шанс через нижнюю сетку.',
  },
  {
    k: 'ROUND_ROBIN',
    name: 'Round-Robin',
    desc: 'Круговая. Каждый играет с каждым. Лучше всего для 4–10 участников.',
  },
  {
    k: 'SWISS',
    name: 'Swiss',
    desc: 'Швейцарка. Пары формируются по очкам после каждого тура.',
  },
  {
    k: 'GROUPS_PLAYOFF',
    name: 'Группы + плей-офф',
    desc: 'Сначала круг в группах, затем сетка из лучших.',
  },
];

const MATCH_FORMAT_HELP: Record<MatchFormat, string> = {
  BO3: 'До 2 побед (макс. 3 сета)',
  BO5: 'До 3 побед (макс. 5 сетов)',
  BO7: 'До 4 побед (макс. 7 сетов)',
};

export function TournamentNew() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [discipline, setDiscipline] = useState<Discipline>('SINGLES');
  const [format, setFormat] = useState<TournamentFormat>('SINGLE_ELIM');
  const [matchFormat, setMatchFormat] = useState<MatchFormat>('BO5');
  const [scoreMode, setScoreMode] = useState<ScoreMode>('SETS');
  const [groupSize, setGroupSize] = useState<GroupConfig['groupSize']>(4);
  const [advancePerGroup, setAdvancePerGroup] = useState<GroupConfig['advancePerGroup']>(2);

  async function handleSubmit() {
    if (!user) return;

    const parsed = newTournamentSchema.safeParse({
      name,
      date,
      discipline,
      format,
      matchFormat,
      scoreMode,
    });
    if (!parsed.success) {
      toast.error('Проверьте поля', { description: parsed.error.issues[0].message });
      return;
    }

    setSubmitting(true);
    try {
      const id = await createTournament({
        name: parsed.data.name,
        date: new Date(parsed.data.date),
        ownerUid: user.uid,
        discipline: parsed.data.discipline,
        format: parsed.data.format,
        matchFormat: parsed.data.matchFormat,
        scoreMode: parsed.data.scoreMode,
        ...(parsed.data.format === 'GROUPS_PLAYOFF'
          ? {
              groupConfig: {
                groupSize,
                advancePerGroup,
                playoffFormat: 'SINGLE_ELIM' as const,
              },
            }
          : {}),
      });
      toast.success('Турнир создан');
      navigate(`/tournaments/${id}`);
    } catch (err) {
      toast.error('Не получилось создать', { description: String((err as Error).message) });
      setSubmitting(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <AppHeader showUser />
      <div
        className="container-d"
        style={{ paddingTop: 24, paddingBottom: 64, maxWidth: 760 }}
      >
        <Link
          to="/tournaments"
          className="btn btn-ghost btn-sm"
          style={{ marginBottom: 12, paddingLeft: 8 }}
        >
          <ArrowLeft size={14} /> К турнирам
        </Link>
        <h1 className="td-h1" style={{ marginBottom: 6 }}>
          Новый турнир
        </h1>
        <div className="t-small t-mute" style={{ marginBottom: 28 }}>
          Большую часть параметров можно изменить позже, но формат — нет.
        </div>

        {/* Основное */}
        <div className="card-d card-pad" style={{ marginBottom: 16 }}>
          <div className="t-eyebrow" style={{ marginBottom: 14 }}>
            Основное
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label className="label">Название турнира</label>
              <input
                className="input"
                placeholder="Кубок города 2026"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Дата проведения</label>
              <input
                className="input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Дисциплина</label>
              <div className="seg" style={{ width: '100%' }}>
                <button
                  aria-selected={discipline === 'SINGLES'}
                  onClick={() => setDiscipline('SINGLES')}
                  style={{ flex: 1 }}
                >
                  Одиночка
                </button>
                <button
                  aria-selected={discipline === 'DOUBLES'}
                  onClick={() => setDiscipline('DOUBLES')}
                  style={{ flex: 1 }}
                >
                  Пары
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Формат турнира */}
        <div className="card-d card-pad" style={{ marginBottom: 16 }}>
          <div className="t-eyebrow" style={{ marginBottom: 14 }}>
            Формат турнира
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 10,
            }}
          >
            {FORMATS.map((f) => (
              <button
                key={f.k}
                className="fmt-card"
                aria-selected={format === f.k}
                onClick={() => setFormat(f.k)}
              >
                <div className="fmt-name">{f.name}</div>
                <div className="fmt-desc">{f.desc}</div>
              </button>
            ))}
          </div>

          {format === 'SWISS' && (
            <div className="help" style={{ marginTop: 12 }}>
              Число туров рассчитается автоматически как ceil(log2(N)) — например, для 16
              игроков → 4 тура.
            </div>
          )}

          {format === 'GROUPS_PLAYOFF' && (
            <div
              style={{
                marginTop: 16,
                padding: 14,
                background: 'var(--bg-sunken)',
                border: '1px dashed var(--line-strong)',
                borderRadius: 'var(--r-3)',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 14,
              }}
            >
              <div>
                <label className="label">Размер группы</label>
                <select
                  className="select"
                  value={groupSize}
                  onChange={(e) =>
                    setGroupSize(Number(e.target.value) as GroupConfig['groupSize'])
                  }
                >
                  {[3, 4, 5, 6].map((v) => (
                    <option key={v} value={v}>
                      {v} игроков
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Выходит из группы</label>
                <select
                  className="select"
                  value={advancePerGroup}
                  onChange={(e) =>
                    setAdvancePerGroup(
                      Number(e.target.value) as GroupConfig['advancePerGroup'],
                    )
                  }
                >
                  {[1, 2].map((v) => (
                    <option key={v} value={v}>
                      Топ-{v}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Формат матча */}
        <div className="card-d card-pad" style={{ marginBottom: 28 }}>
          <div className="t-eyebrow" style={{ marginBottom: 14 }}>
            Формат матча
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label className="label">До скольких побед</label>
              <div className="seg" style={{ width: '100%' }}>
                {(['BO3', 'BO5', 'BO7'] as MatchFormat[]).map((bo) => (
                  <button
                    key={bo}
                    aria-selected={matchFormat === bo}
                    onClick={() => setMatchFormat(bo)}
                    style={{ flex: 1 }}
                  >
                    {bo}
                  </button>
                ))}
              </div>
              <div className="help">{MATCH_FORMAT_HELP[matchFormat]}</div>
            </div>
            <div>
              <label className="label">Учёт счёта</label>
              <div className="seg" style={{ width: '100%' }}>
                <button
                  aria-selected={scoreMode === 'WINNER_ONLY'}
                  onClick={() => setScoreMode('WINNER_ONLY')}
                  style={{ flex: 1 }}
                >
                  Только победитель
                </button>
                <button
                  aria-selected={scoreMode === 'SETS'}
                  onClick={() => setScoreMode('SETS')}
                  style={{ flex: 1 }}
                >
                  По сетам
                </button>
              </div>
              <div className="help">
                {scoreMode === 'WINNER_ONLY'
                  ? 'Записывается только победа/поражение'
                  : 'Записывается счёт по сетам'}
              </div>
            </div>
          </div>
        </div>

        <div className="row gap-3" style={{ justifyContent: 'flex-end' }}>
          <Link to="/tournaments" className="btn">
            Отмена
          </Link>
          <button
            className="btn btn-primary"
            disabled={!name.trim() || submitting}
            onClick={handleSubmit}
          >
            {submitting ? (
              <>
                <span className="spinner" /> Создаём...
              </>
            ) : (
              <>
                <Plus size={16} /> Создать турнир
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
